import { useId, useState } from 'react';
import axios from 'axios';
import QRCode from 'react-qr-code';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldHalved, faCircleCheck, faTriangleExclamation, faCopy } from '@fortawesome/free-solid-svg-icons';
import Modal from '../Modal/Modal';
import Form from '../Form/Form';
import Button from '../Form/Button';
import InputText from '../Form/Fields/InputText';
import InputPassword from '../Form/Fields/InputPassword';
import { useAuth } from '../../context/AuthProvider';
import { API_BASE_URL } from '../../api';
import '../Form/Fields/Switcher.scss';
import './TwoFactorSection.scss';

const TwoFactorSection = ({ user }) => {
  const { login } = useAuth();
  const [showSetup, setShowSetup] = useState(false);
  const [showDisable, setShowDisable] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [loading, setLoading] = useState(false);

  // Rendered both on the full /account page and inside the sidebar's quick AccountModal,
  // which can be mounted at the same time — unique per-instance ids keep the two modal/form
  // pairs from colliding in the DOM (same reasoning as DeactivateAccountSection).
  const uid = useId();
  const enableFormId = `twoFactorEnableForm-${uid}`;
  const disableFormId = `twoFactorDisableForm-${uid}`;

  const openSetup = () => {
    setLoading(true);
    axios.post(`${API_BASE_URL}/api/auth/2fa/setup`, {}, { withCredentials: true })
      .then(({ data }) => {
        setSetupData(data);
        setShowSetup(true);
      })
      .catch(() => toast.error('Impossible de démarrer la configuration.'))
      .finally(() => setLoading(false));
  };

  const closeSetup = () => {
    setShowSetup(false);
    setSetupData(null);
    setRecoveryCodes(null);
  };

  const confirmEnable = (data) => {
    setLoading(true);
    axios.post(`${API_BASE_URL}/api/auth/2fa/enable`, { code: data.code }, { withCredentials: true })
      .then(({ data: result }) => {
        toast.success('Authentification à deux facteurs activée.');
        // Refresh user.twoFactorEnabled right away, but keep the modal open one more step —
        // these recovery codes are shown exactly once and never retrievable again afterwards.
        login();
        setRecoveryCodes(result.recoveryCodes || []);
      })
      .catch((error) => toast.error(error?.response?.data?.message || 'Code invalide.'))
      .finally(() => setLoading(false));
  };

  const copySecret = () => {
    navigator.clipboard?.writeText(setupData.secret);
    toast.success('Clé copiée.');
  };

  const confirmDisable = (data) => {
    setLoading(true);
    axios.post(`${API_BASE_URL}/api/auth/2fa/disable`, data, { withCredentials: true })
      .then(() => {
        toast.success('Authentification à deux facteurs désactivée.');
        setShowDisable(false);
        login();
      })
      .catch((error) => toast.error(error?.response?.data?.message || 'Une erreur est survenue.'))
      .finally(() => setLoading(false));
  };

  return (
    <div className="account-2fa">
      <div className="account-2fa__header">
        <span className="account-2fa__icon">
          <FontAwesomeIcon icon={faShieldHalved} />
        </span>
        <div>
          <div className="account-2fa__title">Authentification à deux facteurs (2FA)</div>
          <p className="account-2fa__desc">
            Ajoutez une couche de sécurité supplémentaire : une fois activée, un code généré par
            une application d'authentification (Google Authenticator, Authy…) vous sera demandé
            à chaque connexion.
          </p>
        </div>
        <div className="switcher-wrapper mini" style={{ flexShrink: 0, marginLeft: 'auto', alignSelf: 'flex-start' }}>
          <div
            className="switcher"
            role="switch"
            aria-checked={user.twoFactorEnabled}
            aria-label={user.twoFactorEnabled ? 'Désactiver la 2FA' : 'Activer la 2FA'}
            onClick={loading ? undefined : (user.twoFactorEnabled ? () => setShowDisable(true) : openSetup)}
            style={loading ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
          >
            <div
              className="switcher-knob"
              style={{ transform: `translateX(${user.twoFactorEnabled ? 20 : 0}px)`, transition: 'transform 0.2s ease' }}
            />
          </div>
        </div>
      </div>

      <Modal
        id={`twoFactorSetupModal-${uid}`}
        title={recoveryCodes ? 'Vos codes de récupération' : "Configurer l'authentification à deux facteurs"}
        show={showSetup}
        onClose={closeSetup}
        icon={recoveryCodes ? faTriangleExclamation : faShieldHalved}
        iconBg={recoveryCodes ? '#fffbeb' : '#eff6ff'}
        iconColor={recoveryCodes ? '#d97706' : '#2563eb'}
        footer={
          recoveryCodes ? (
            <button type="button" className="modal-btn-primary" onClick={closeSetup}>
              <FontAwesomeIcon icon={faCircleCheck} />
              J'ai sauvegardé mes codes
            </button>
          ) : (
            <Button
              isLoading={loading}
              loadingMessage="Vérification…"
              className="modal-btn-primary"
              type="submit"
              form={enableFormId}
            >
              <FontAwesomeIcon icon={faCircleCheck} />
              Activer
            </Button>
          )
        }
      >
        {recoveryCodes ? (
          <div className="account-2fa__recovery">
            <p className="account-2fa__recovery-warning">
              Notez ces codes et conservez-les en lieu sûr : chacun ne peut être utilisé qu'une
              seule fois pour vous connecter si vous perdez l'accès à votre application
              d'authentification. Ils ne seront plus jamais affichés.
            </p>
            <div className="account-2fa__recovery-codes">
              {recoveryCodes.map((code) => (
                <code key={code}>{code}</code>
              ))}
            </div>
          </div>
        ) : setupData && (
          <div className="account-2fa__setup">
            <ol className="account-2fa__steps">
              <li><span className="account-2fa__step-num">1</span>Ouvrez votre application d'authentification (Google Authenticator, Authy…).</li>
              <li><span className="account-2fa__step-num">2</span>Scannez ce QR code, ou saisissez la clé manuellement.</li>
              <li><span className="account-2fa__step-num">3</span>Saisissez le code à 6 chiffres généré pour confirmer.</li>
            </ol>

            <div className="account-2fa__qr-card">
              <div className="account-2fa__qr">
                <QRCode value={setupData.otpauthUri} size={160} />
              </div>

              <div className="account-2fa__divider"><span>ou saisissez la clé manuellement</span></div>

              <div className="account-2fa__secret">
                <code>{setupData.secret}</code>
                <button type="button" className="account-2fa__copy" onClick={copySecret} title="Copier la clé">
                  <FontAwesomeIcon icon={faCopy} />
                </button>
              </div>
            </div>

            <Form id={enableFormId} onSubmit={confirmEnable}>
              <span className="cdm-field-label">Code de vérification <span className="text-danger">*</span></span>
              <InputText
                name="code"
                placeholder="123456"
                className="account-2fa__code-input"
                validationRules={{ required: 'Le code est obligatoire' }}
              />
            </Form>
          </div>
        )}
      </Modal>

      <Modal
        id={`twoFactorDisableModal-${uid}`}
        title="Désactiver la 2FA ?"
        show={showDisable}
        onClose={() => setShowDisable(false)}
        icon={faShieldHalved}
        iconBg="#fef2f2"
        iconColor="#dc2626"
        footer={
          <Button
            isLoading={loading}
            loadingMessage="Désactivation…"
            className="modal-btn-danger"
            type="submit"
            form={disableFormId}
          >
            Désactiver la 2FA
          </Button>
        }
      >
        <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 16px', lineHeight: 1.6 }}>
          Un code ne sera plus demandé à la connexion. Confirmez votre mot de passe actuel pour
          continuer.
        </p>
        <Form id={disableFormId} onSubmit={confirmDisable}>
          <span className="cdm-field-label">Mot de passe actuel <span className="text-danger">*</span></span>
          <InputPassword
            name="currentPassword"
            placeholder="Mot de passe actuel"
            validationRules={{ required: 'Le mot de passe est obligatoire' }}
          />
        </Form>
      </Modal>
    </div>
  );
};

export default TwoFactorSection;
